import { Fragment, ReactNode } from "react";
import type { QuizQuestion } from "@core/features/quiz";
import type { ProcessedAnswer } from "../../publicProfile.types";
import { processQuestion } from "../../utils";

/**
 * Props passed to renderItem render prop.
 */
export type RenderItemProps = {
  answer: ProcessedAnswer;
  index: number;
};

/**
 * Props for QuizResponsePreviewBase component.
 */
export type Props = {
  /** Quiz questions with answers merged in */
  questions: QuizQuestion[];
  /** Filter questions by internal name (optional) */
  filterByInternalNames?: string[];
  /** Whether to hide questions without answers */
  hideUnanswered?: boolean;
  /** Render each answer item */
  renderItem: (props: RenderItemProps) => ReactNode;
  /** Render empty state when no answers */
  renderEmpty?: () => ReactNode;
  /** Additional CSS class */
  className?: string;
  /** Test ID */
  "data-testid"?: string;
};

/**
 * Base component for displaying quiz response answers in a read-only preview.
 * Uses render props for complete UI customization.
 *
 * @example
 * ```typescript
 * <QuizResponsePreviewBase
 *   questions={quizQuestions}
 *   hideUnanswered
 *   renderItem={({ answer }) => (
 *     <div>
 *       <label>{answer.label}</label>
 *       {answer.textValue && <p>{answer.textValue}</p>}
 *       {answer.selectedChoices?.map(choice => <span>{choice}</span>)}
 *       {answer.uploadedMedia?.map(media => <img src={media.file} />)}
 *     </div>
 *   )}
 * />
 * ```
 */
export function QuizResponsePreviewBase({
  questions,
  filterByInternalNames,
  hideUnanswered = false,
  renderItem,
  renderEmpty,
  className,
  "data-testid": testId,
}: Props) {
  // Process and filter questions
  let processedAnswers = questions.map(processQuestion);

  // Filter by internal names if specified
  if (filterByInternalNames && filterByInternalNames.length > 0) {
    processedAnswers = processedAnswers.filter((answer) =>
      filterByInternalNames.includes(answer.internalName)
    );
  }

  // Filter out unanswered if requested
  if (hideUnanswered) {
    processedAnswers = processedAnswers.filter((answer) => answer.hasAnswer);
  }

  // Show empty state
  if (processedAnswers.length === 0) {
    return renderEmpty?.() ?? null;
  }

  return (
    <div className={className} data-testid={testId ?? "quiz-response-preview"}>
      {processedAnswers.map((answer, index) => (
        <Fragment key={answer.questionId}>
          {renderItem({ answer, index })}
        </Fragment>
      ))}
    </div>
  );
}

export default QuizResponsePreviewBase;
